/**
 * EmmaEntityContext.tsx
 * Displays entities extracted from Emma responses
 * Story 3 (RAG-002-003): Frontend Entity Context
 * 
 * Features:
 * - Entity cards with type badges
 * - Organization isolation (org_id filtering)
 * - Responsive design (mobile, tablet, desktop)
 * - Accessibility (ARIA labels, keyboard nav)
 * - Real-time entity extraction from Emma messages
 */

import React, { useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { AlertCircle, Users, Target, Zap, BookOpen, Layers } from 'lucide-react';

// Entity type icons and colors
const ENTITY_CONFIG = {
  client: {
    icon: Users,
    color: 'bg-blue-100 text-blue-800',
    badgeColor: 'bg-blue-200',
    label: '🏢 Client',
  },
  partner: {
    icon: Zap,
    color: 'bg-purple-100 text-purple-800',
    badgeColor: 'bg-purple-200',
    label: '⚡ Partner',
  },
  solution: {
    icon: Target,
    color: 'bg-green-100 text-green-800',
    badgeColor: 'bg-green-200',
    label: '🎯 Solution',
  },
  icp: {
    icon: Layers,
    color: 'bg-orange-100 text-orange-800',
    badgeColor: 'bg-orange-200',
    label: '📊 ICP',
  },
  persona: {
    icon: BookOpen,
    color: 'bg-pink-100 text-pink-800',
    badgeColor: 'bg-pink-200',
    label: '👤 Persona',
  },
  campaign: {
    icon: Target,
    color: 'bg-indigo-100 text-indigo-800',
    badgeColor: 'bg-indigo-200',
    label: '📢 Campaign',
  },
  content: {
    icon: BookOpen,
    color: 'bg-teal-100 text-teal-800',
    badgeColor: 'bg-teal-200',
    label: '📝 Content',
  },
  asset: {
    icon: Zap,
    color: 'bg-cyan-100 text-cyan-800',
    badgeColor: 'bg-cyan-200',
    label: '📦 Asset',
  },
};

export interface EntityReference {
  id: string;
  type: keyof typeof ENTITY_CONFIG;
  name: string;
  description?: string;
  similarity_score?: number;
  metadata?: Record<string, any>;
}

export interface EmmaEntityContextProps {
  entities: EntityReference[];
  onEntitySelect?: (entity: EntityReference) => void;
  isLoading?: boolean;
  orgId?: string;
  className?: string;
}

/**
 * Entity Card Component
 * Displays individual entity with type badge and metadata
 */
const EntityCard: React.FC<{
  entity: EntityReference;
  onSelect?: (entity: EntityReference) => void;
  isSelected?: boolean;
}> = ({ entity, onSelect, isSelected = false }) => {
  const config = ENTITY_CONFIG[entity.type];
  const Icon = config.icon;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      }`}
      onClick={() => onSelect?.(entity)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(entity);
        }
      }}
      aria-label={`${config.label}: ${entity.name}`}
      aria-pressed={isSelected}
    >
      {/* Header: Icon + Name + Badge */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${config.color}`} aria-hidden="true" />
          <span className="font-semibold text-sm text-gray-900">{entity.name}</span>
        </div>
        <Badge className={config.badgeColor}>{config.label}</Badge>
      </div>

      {/* Description */}
      {entity.description && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{entity.description}</p>
      )}

      {/* Similarity Score */}
      {entity.similarity_score !== undefined && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Similarity</span>
          <div className="flex items-center gap-1">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${(entity.similarity_score || 0) * 100}%` }}
                aria-hidden="true"
              />
            </div>
            <span className="w-8 text-right">{(entity.similarity_score * 100).toFixed(0)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Main EmmaEntityContext Component
 */
export const EmmaEntityContext: React.FC<EmmaEntityContextProps> = ({
  entities = [],
  onEntitySelect,
  isLoading = false,
  orgId,
  className = '',
}) => {
  const [selectedEntityId, setSelectedEntityId] = React.useState<string | null>(null);

  // Filter entities by org if provided
  const filteredEntities = useMemo(() => {
    if (!orgId) return entities;
    return entities.filter((e) => !e.metadata?.org_id || e.metadata.org_id === orgId);
  }, [entities, orgId]);

  // Group entities by type
  const groupedEntities = useMemo(() => {
    const groups: Record<string, EntityReference[]> = {};
    filteredEntities.forEach((entity) => {
      if (!groups[entity.type]) {
        groups[entity.type] = [];
      }
      groups[entity.type].push(entity);
    });
    return groups;
  }, [filteredEntities]);

  const handleEntitySelect = useCallback(
    (entity: EntityReference) => {
      setSelectedEntityId(entity.id);
      onEntitySelect?.(entity);
    },
    [onEntitySelect],
  );

  if (isLoading) {
    return (
      <div
        className={`p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-sm text-gray-600 ${className}`}
        role="status"
        aria-label="Loading entities"
      >
        Loading entities...
      </div>
    );
  }

  if (filteredEntities.length === 0) {
    return (
      <div
        className={`p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2 text-sm text-gray-600 ${className}`}
        role="status"
      >
        <AlertCircle className="w-4 h-4" />
        No entities found in this context
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`} role="region" aria-label="Entity context">
      {Object.entries(groupedEntities).map(([type, typeEntities]) => (
        <div key={type} className="space-y-2">
          {/* Type Header */}
          <h3 className="text-sm font-semibold text-gray-700 px-1">
            {ENTITY_CONFIG[type as keyof typeof ENTITY_CONFIG]?.label} ({typeEntities.length})
          </h3>

          {/* Entity Cards Grid - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {typeEntities.map((entity) => (
              <EntityCard
                key={entity.id}
                entity={entity}
                onSelect={handleEntitySelect}
                isSelected={selectedEntityId === entity.id}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const extractEmmaEntities = (message: string): EntityReference[] => {
  // Pattern: [Entity Type] entity_name (entity_id)
  // Example: [Client] Acme Corp (uuid-here)
  const entityPattern = /\[(\w+)\]\s+([^\(]+)\s*\(([a-f0-9-]+)\)/g;
  const extracted: EntityReference[] = [];

  let match;
  while ((match = entityPattern.exec(message)) !== null) {
    const [, type, name, id] = match;
    const normalizedType = type.toLowerCase();
    if (normalizedType in ENTITY_CONFIG) {
      extracted.push({
        id,
        type: normalizedType as keyof typeof ENTITY_CONFIG,
        name: name.trim(),
      });
    }
  }

  return extracted;
};

/**
 * Hook for extracting entities from Emma message
 * Used in EmmaChat component to capture entities from response
 */
export const useEmmaEntityExtraction = (message: string): EntityReference[] => {
  return useMemo(() => extractEmmaEntities(message), [message]);
};

export default EmmaEntityContext;
